/**
 * useSeenDemotion Hook
 * 
 * Combines Redux state, mark seen, and ordering utilities
 * to provide a complete seen-demotion experience.
 * 
 * STATE CONSOLIDATION: Uses Redux (seenHistorySlice) as single source of truth.
 * 
 * LINKEDIN MODEL: Feed order is frozen on load. Seen tracking still happens,
 * but ordering only updates on explicit refresh (pull-to-refresh / home button click).
 * This prevents scroll jumps during viewing.
 */
import { useCallback, useRef, useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useMarkSeen } from './useMarkSeen';
import { applySeenDemotion, layerProjectsFeed } from '../utils/feedOrderingUtils';
import { selectSeenHistory, markItemSeen } from '../slices/seenHistorySlice';

export const useSeenDemotion = (feedType = 'main') => {
    const currentUser = useSelector(state => state.auth.currentUser);
    const dispatch = useDispatch();
    const store = useStore();

    // Single source of truth: Redux state (Object format)
    const seenHistory = useSelector(selectSeenHistory);

    const { markAsSeen: markAsSeenRemote } = useMarkSeen();
    const observerRef = useRef(null);
    const visibleItemsRef = useRef(new Set());

    // LINKEDIN MODEL: Frozen snapshot - only updates on explicit refresh
    // This prevents feed reordering while user is viewing/scrolling
    const [frozenSeenHistory, setFrozenSeenHistory] = useState(() => ({ ...seenHistory }));

    /**
     * Refresh the frozen snapshot with current seenHistory
     * Call this on manual refresh (home button click, pull-to-refresh)
     */
    const refreshOrderingSnapshot = useCallback(() => {
        const history = selectSeenHistory(store.getState());
        setFrozenSeenHistory({ ...history });
    }, [store]);

    /**
     * Mark an item as seen (updates Redux state + queues for remote)
     * Note: This still tracks seen items, but doesn't trigger reorder
     */
    const markAsSeen = useCallback(async (itemId, itemType = 'post') => {
        if (!itemId || !currentUser?.uid) return;

        // Update Redux state immediately (synchronous, single source of truth)
        dispatch(markItemSeen({ itemId, itemType }));

        // Queue for remote sync
        markAsSeenRemote(itemId, itemType);
    }, [currentUser?.uid, dispatch, markAsSeenRemote]);

    /**
     * Apply seen-demotion ordering to items
     * Uses FROZEN snapshot to prevent reordering during viewing
     * @param {Array} items - Feed items to order
     * @param {string} [priorityPostId] - Optional post to place at top (for trending clicks)
     */
    const orderItems = useCallback((items, priorityPostId = null) => {
        if (feedType === 'projects') {
            // Projects feed doesn't use priority post (only projects, not posts)
            return layerProjectsFeed(items, currentUser?.uid, [], frozenSeenHistory);
        }
        return applySeenDemotion(items, frozenSeenHistory, priorityPostId);
    }, [feedType, currentUser?.uid, frozenSeenHistory]);

    /**
     * Set up IntersectionObserver for visibility tracking
     */
    const timerMapRef = useRef(new Map()); // Track timers by itemId to prevent memory leaks

    const setupObserver = useCallback((containerRef) => {
        // Return noop cleanup if container not ready
        if (!containerRef?.current) {
            return () => { };
        }

        // Clean up existing observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    const itemId = entry.target.dataset?.itemId;
                    const itemType = entry.target.dataset?.itemType || 'post';

                    if (entry.isIntersecting && itemId) {
                        // Item became visible - add to visible set
                        visibleItemsRef.current.add(itemId);

                        // Clear existing timer for this item if any
                        if (timerMapRef.current.has(itemId)) {
                            clearTimeout(timerMapRef.current.get(itemId));
                        }

                        // Mark as seen after brief visibility (500ms)
                        const timerId = setTimeout(() => {
                            if (visibleItemsRef.current.has(itemId)) {
                                markAsSeen(itemId, itemType);
                            }
                            timerMapRef.current.delete(itemId);
                        }, 500);
                        timerMapRef.current.set(itemId, timerId);
                    } else if (itemId) {
                        // Item left viewport - cancel pending timer
                        visibleItemsRef.current.delete(itemId);
                        if (timerMapRef.current.has(itemId)) {
                            clearTimeout(timerMapRef.current.get(itemId));
                            timerMapRef.current.delete(itemId);
                        }
                    }
                });
            },
            { threshold: 0.5 } // 50% visible
        );

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            // Clear all pending timers on cleanup
            timerMapRef.current.forEach(id => clearTimeout(id));
            timerMapRef.current.clear();
        };
    }, [markAsSeen]);

    /**
     * Observe a feed item element
     */
    const observeItem = useCallback((element) => {
        if (observerRef.current && element) {
            observerRef.current.observe(element);
        }
    }, []);

    /**
     * Stop observing a feed item element
     */
    const unobserveItem = useCallback((element) => {
        if (observerRef.current && element) {
            observerRef.current.unobserve(element);
        }
    }, []);

    return {
        seenHistory,
        markAsSeen,
        orderItems,
        refreshOrderingSnapshot,  // NEW: Call on manual refresh
        setupObserver,
        observeItem,
        unobserveItem
    };
};
